/**
 * Spec 217 PR1 — Statistics dashboard route (`/stats`).
 *
 * Mirror of the mobile bottom-sheet modal (`stats-modal` inside
 * `mobile/src/screens/WorkListScreen.tsx`, lines 1344–1446) re-cast
 * as a top-level web route. The web has more horizontal real estate
 * than a 6"-tall sheet so we lay the cards out in a grid instead of
 * stacking them, but the KPIs and the math come from the same
 * `computeWorkStats` helper that mirrors mobile's `calculateStats`.
 *
 * Why a route over a modal:
 *   - Cmd+K + sidebar both work better with addressable pages.
 *   - A refresh keeps the user on the dashboard instead of
 *     bouncing back to the list (mobile users don't notice — they
 *     don't refresh).
 *   - Future "share /stats?range=month" links become trivial.
 *
 * Read path:
 *   `getWorks(true)` (include archived to match mobile's
 *   `await getWorks(true)` in `openStats`) → filter archived in the
 *   helper. Photos are read separately via `getAllPhotos()` so the
 *   "Fotos totales" KPI is computed from the same source `db.ts`
 *   uses for the WorkDetail gallery. Both reads are O(N) over the
 *   in-memory cache after first paint.
 *
 * Live updates:
 *   `subscribeStore()` re-runs the load when works/photos mutate
 *   so an incoming relay sync doesn't leave the dashboard stale.
 *   No debounce — re-render is cheap when the helper is pure.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Briefcase,
  Calendar,
  Camera,
  ChevronLeft,
  Clock,
  Timer,
  TrendingUp,
  Users,
} from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import { getAllPhotos, getWorks, subscribeStore } from '../store/db';
import type { WebIdentity } from '../store/keys';
import type { Work } from '../types';
import { computeWorkStats, formatDurationMs, type WorkStats } from '../utils/stats';

import './Stats.css';

interface StatsProps {
  identity: WebIdentity;
}

interface LoadedSnapshot {
  works: Work[];
  photoCount: number;
}

const STATUS_COPY: Array<{
  key: keyof Pick<WorkStats, 'pendingCount' | 'inProgressCount' | 'completedCount'>;
  label: string;
  status: Work['status'];
  cssClass: string;
  testId: string;
}> = [
  // Order matches the mobile modal: pending → in_progress → completed.
  {
    key: 'pendingCount',
    label: 'Pendientes',
    status: 'pending',
    cssClass: 'stats-bar--pending',
    testId: 'stats-bar-pending',
  },
  {
    key: 'inProgressCount',
    label: 'En curso',
    status: 'in_progress',
    cssClass: 'stats-bar--in-progress',
    testId: 'stats-bar-in-progress',
  },
  {
    key: 'completedCount',
    label: 'Completados',
    status: 'completed',
    cssClass: 'stats-bar--completed',
    testId: 'stats-bar-completed',
  },
];

export function Stats({ identity }: StatsProps) {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<LoadedSnapshot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [range, setRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  // Load both works + photos in parallel. We pass `includeArchived=true`
  // to `getWorks` so the snapshot is exhaustive — the helper drops
  // archived rows itself, but the photo count is taken across all
  // works (including archived) since the mobile counterpart does the
  // same (see `WorkListScreen.tsx` photoCount initialisation).
  useEffect(() => {
    let cancelled = false;
    async function reload() {
      try {
        const [works, photos] = await Promise.all([getWorks(true), getAllPhotos()]);
        if (cancelled) return;
        setSnapshot({ works, photoCount: photos.length });
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Error al cargar');
      }
    }
    void reload();
    const off = subscribeStore(() => {
      void reload();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  // Range filter — compares `work.updatedAt` against a cutoff so the
  // KPIs reflect only the selected window. `all` skips the filter so
  // the helper sees the full dataset (matches the original behaviour).
  const filteredWorks = useMemo<Work[] | null>(() => {
    if (!snapshot) return null;
    if (range === 'all') return snapshot.works;
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const cutoffMs = Date.now() - days * 24 * 3600 * 1000;
    return snapshot.works.filter((w) => {
      const updated = typeof w.updatedAt === 'number' ? w.updatedAt : 0;
      return updated >= cutoffMs;
    });
  }, [snapshot, range]);

  const stats = useMemo<WorkStats | null>(() => {
    if (!filteredWorks) return null;
    return computeWorkStats(filteredWorks);
  }, [filteredWorks]);

  const RANGE_OPTIONS: Array<{ value: '7d' | '30d' | '90d' | 'all'; label: string }> = [
    { value: '7d', label: '7d' },
    { value: '30d', label: '30d' },
    { value: '90d', label: '90d' },
    { value: 'all', label: 'Todo' },
  ];

  // Title for the "this month" card — mobile renders the same string
  // ("abril de 2026" in es-ES). We do it client-side so the locale
  // follows the browser. Recomputed on each render is fine — it's a
  // few microseconds.
  const monthLabel = useMemo(() => {
    return new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  }, []);

  // The status bar widths are computed against the largest count so a
  // team of 1 pending / 0 in-progress / 0 completed still renders the
  // pending bar full-width. Identical to mobile (`maxStatus`).
  const maxStatusCount = stats
    ? Math.max(stats.pendingCount, stats.inProgressCount, stats.completedCount, 1)
    : 1;

  // Identity is unused below the header today. `_` suppresses the
  // unused-var lint without renaming the prop (we keep the
  // parameter name for parity with sibling routes — every paired
  // route receives `identity`).
  void identity;

  return (
    <div className="stats-shell" data-testid="stats-screen">
      <header className="stats-header">
        <button
          type="button"
          className="stats-back"
          onClick={() => navigate(-1)}
          data-testid="stats-back"
        >
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </button>
        <h1 className="stats-title">
          <BarChart3 size={20} strokeWidth={2} aria-hidden="true" />
          Estadísticas
        </h1>
        <div
          className="stats-range-toggle"
          role="group"
          aria-label="Rango de fechas"
          data-testid="stats-range-toggle"
        >
          {RANGE_OPTIONS.map((opt) => {
            const active = range === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`stats-range-btn${active ? ' stats-range-btn--active' : ''}`}
                onClick={() => setRange(opt.value)}
                aria-pressed={active}
                data-testid={`stats-range-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </header>

      <main id="main-content" className="stats-main" tabIndex={-1}>
        {/* sr-only h1 — see Settings/WorkList for rationale (route
            header h1 is hidden by AppShell paired layout). */}
        <h1 className="sr-only">Estadísticas</h1>
        {loadError ? (
          <div className="stats-error" data-testid="stats-error">
            <p>{loadError}</p>
          </div>
        ) : !stats ? (
          <div className="stats-loading" data-testid="stats-loading">
            <p>Cargando estadísticas…</p>
          </div>
        ) : stats.total === 0 ? (
          <EmptyState
            testId="stats-empty"
            variant="noWorks"
            title="Aún no hay datos"
            body={
              <>
                Crea o recibe un trabajo y volverás aquí para ver el resumen
                de actividad. Las estadísticas se calculan en local desde tus
                trabajos sincronizados.
              </>
            }
            action={{
              label: 'Crear trabajo',
              onClick: () => navigate('/works/new'),
              testId: 'stats-empty-create',
            }}
          />
        ) : (
          <div className="stats-grid">
            {/* Total works — hero card. */}
            <section className="stats-card stats-card--hero" data-testid="stats-total">
              <div className="stats-card-header">
                <Briefcase size={20} strokeWidth={2} aria-hidden="true" />
                <span className="stats-card-label">Total trabajos</span>
              </div>
              <p className="stats-value-large" data-testid="stats-total-value">
                {stats.total}
              </p>
              <p className="stats-card-foot">trabajos activos en tu equipo</p>
            </section>

            {/* By status — bars + tap-to-filter links. */}
            <section
              className="stats-card stats-card--by-status"
              data-testid="stats-by-status"
            >
              <div className="stats-card-header">
                <TrendingUp size={20} strokeWidth={2} aria-hidden="true" />
                <span className="stats-card-label">Por estado</span>
              </div>
              <ul className="stats-bar-list">
                {STATUS_COPY.map((row) => {
                  const value = stats[row.key];
                  const pct = (value / maxStatusCount) * 100;
                  return (
                    <li key={row.key}>
                      <Link
                        to={`/?status=${row.status}`}
                        className="stats-bar-link"
                        data-testid={row.testId}
                      >
                        <div className="stats-bar-info">
                          <span className="stats-bar-label">{row.label}</span>
                          <span
                            className={`stats-bar-value ${row.cssClass}`}
                            data-testid={`${row.testId}-value`}
                          >
                            {value}
                          </span>
                        </div>
                        <div className="stats-bar-track">
                          <span
                            className={`stats-bar-fill ${row.cssClass}`}
                            style={{ width: `${pct}%` }}
                            aria-hidden="true"
                          />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>

            {/* This month — created + completed deltas. */}
            <section className="stats-card" data-testid="stats-this-month">
              <div className="stats-card-header">
                <Calendar size={20} strokeWidth={2} aria-hidden="true" />
                <span className="stats-card-label stats-card-label--cap">{monthLabel}</span>
              </div>
              <div className="stats-split">
                <div className="stats-split-block">
                  <span
                    className="stats-value-medium"
                    data-testid="stats-month-created-value"
                  >
                    {stats.createdThisMonth}
                  </span>
                  <span className="stats-split-sub">creados</span>
                </div>
                <div className="stats-split-divider" aria-hidden="true" />
                <div className="stats-split-block">
                  <span
                    className="stats-value-medium"
                    data-testid="stats-month-completed-value"
                  >
                    {stats.completedThisMonth}
                  </span>
                  <span className="stats-split-sub">completados</span>
                </div>
              </div>
            </section>

            {/* This week — same shape as the month card so the eye groups them. */}
            <section className="stats-card" data-testid="stats-this-week">
              <div className="stats-card-header">
                <Clock size={20} strokeWidth={2} aria-hidden="true" />
                <span className="stats-card-label">Esta semana</span>
              </div>
              <div className="stats-split">
                <div className="stats-split-block">
                  <span
                    className="stats-value-medium"
                    data-testid="stats-week-created-value"
                  >
                    {stats.createdThisWeek}
                  </span>
                  <span className="stats-split-sub">creados</span>
                </div>
                <div className="stats-split-divider" aria-hidden="true" />
                <div className="stats-split-block">
                  <span
                    className="stats-value-medium"
                    data-testid="stats-week-completed-value"
                  >
                    {stats.completedThisWeek}
                  </span>
                  <span className="stats-split-sub">completados</span>
                </div>
              </div>
            </section>

            {/* Avg time + photos — small cards. */}
            <section className="stats-card stats-card--small" data-testid="stats-avg-time">
              <div className="stats-card-header">
                <Timer size={16} strokeWidth={2} aria-hidden="true" />
                <span className="stats-card-label">Tiempo medio</span>
              </div>
              <p className="stats-value-medium" data-testid="stats-avg-time-value">
                {formatDurationMs(stats.avgTimeMs)}
              </p>
              <p className="stats-card-foot">por trabajo completado</p>
            </section>

            <section className="stats-card stats-card--small" data-testid="stats-total-photos">
              <div className="stats-card-header">
                <Camera size={16} strokeWidth={2} aria-hidden="true" />
                <span className="stats-card-label">Fotos totales</span>
              </div>
              <p className="stats-value-medium" data-testid="stats-total-photos-value">
                {snapshot?.photoCount ?? 0}
              </p>
              <p className="stats-card-foot">en todos los trabajos</p>
            </section>

            {/* Top client — derived. Only render when there's a real
                clientName on at least one work; otherwise the card just
                shows a tasteful "—" without confusing the eye. */}
            <section className="stats-card stats-card--small" data-testid="stats-top-client">
              <div className="stats-card-header">
                <Users size={16} strokeWidth={2} aria-hidden="true" />
                <span className="stats-card-label">Cliente top</span>
              </div>
              {stats.topClient ? (
                <>
                  <p
                    className="stats-value-text"
                    data-testid="stats-top-client-name"
                    title={stats.topClient.name}
                  >
                    {stats.topClient.name}
                  </p>
                  <p className="stats-card-foot" data-testid="stats-top-client-count">
                    {stats.topClient.count}{' '}
                    {stats.topClient.count === 1 ? 'trabajo' : 'trabajos'}
                  </p>
                </>
              ) : (
                <p className="stats-value-text stats-value-text--muted">—</p>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
